import { omit } from 'ramda'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import type { CreateCampaignApiArg } from '@/__generated/es-api'
import { clientRef } from '@/app/client-ref'
import { DEFAULT_SENDER_ID_VALUE } from '@/app/definitions'
import { useCampaignFiles } from '@/app/module/campaigns/component/campaign-files-provider'
import { BackendCampaignTemplate } from '@/app/module/campaigns/types'
import type { ReminderCampaignDTO } from '@/app/module/campaigns/types/dtos'
import type { CampaignSaveData, LegacyCampaignSaveData } from '@/app/module/campaigns/types/save-data'
import { prepareActionsForSaving } from '@/app/module/campaigns/utils/actions/saving'
import { toCampaignCreateBody, toCampaignUpdateBody } from '@/app/module/campaigns/utils/campaign-payload'
import { getCampaignErrorMessage } from '@/app/module/campaigns/utils/error'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { FilesType, FileType } from '@/app/types'

import { useLaunchCampaign } from './use-launch-campaign'

// Prepares main-editor data, creates or updates the campaign, and optionally launches it with its own notifications.
export const useSaveCampaign = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const {
    files: { data: files },
  } = useCampaignFiles()

  const [create, createRes] = esApi.endpoints.createCampaign.useMutation()
  const [update, updateRes] = esApi.endpoints.updateCampaign.useMutation()
  const [launch, launchRes] = useLaunchCampaign()

  const saveCampaign = useCallback(
    async (item: LegacyCampaignSaveData | CampaignSaveData<ReminderCampaignDTO>, toBePublished = false) => {
      let campaignItem: CampaignSaveData<CreateCampaignApiArg['body']>
      const { uiStore, variables } = item

      if (item.template === BackendCampaignTemplate.Reminder) {
        campaignItem = {
          ...item,
          uiStore: {
            ...(uiStore || {}),
            newItems: [],
            files: withoutUrl(files),
          },
        }
      } else {
        const computedVars = prepareActionsForSaving(item)
        const currentSenderId = 'senderId' in variables ? variables.senderId : undefined
        const normalizedSenderId = currentSenderId === DEFAULT_SENDER_ID_VALUE ? '' : currentSenderId || ''

        campaignItem = {
          ...item,
          variables: {
            ...variables,
            ...computedVars,
            senderId: normalizedSenderId,
          },
          uiStore: {
            ...(uiStore || {}),
            newItems: [],
            files: withoutUrl(files), // store the current files state to uiStore
          },
        }
      }

      try {
        // Keep identity in save data; only the final API body excludes it.
        const res = item.id
          ? await update({
              orgId: orgId.toString(),
              campaignId: item.id.toString(),
              body: { ...toCampaignUpdateBody(campaignItem), clientRef },
            }).unwrap()
          : await create({
              orgId: orgId.toString(),
              body: { ...toCampaignCreateBody(campaignItem), clientRef },
            }).unwrap()

        if (!res) {
          return undefined
        }

        if (!toBePublished) {
          dispatch(
            createNotification({
              'save-campaign': {
                type: 'success',
                message: 'Campaign saved successfully.',
              },
            }),
          )
          return res
        }

        await launch(res.id)

        const newCampaign = {
          ...res,
          published: true,
        }

        return newCampaign
      } catch (error) {
        dispatch(
          createNotification({
            'save-campaign': {
              type: 'error',
              message: getCampaignErrorMessage(error, item),
            },
          }),
        )
        return undefined
      }
    },
    [files, orgId, create, dispatch, launch, update],
  )

  const result = {
    isLoading: createRes.isLoading || updateRes.isLoading || launchRes.isLoading,
    isError: createRes.isError || updateRes.isError || launchRes.isError,
    error: createRes.error || updateRes.error || launchRes.error,
    data: createRes.data || updateRes.data,
  }

  return [saveCampaign, result] as const
}

/**
 * @description Remove url property from files since they can contains signed token that won't be valid later
 */
export const withoutUrl = (files: FilesType) =>
  Object.values(files).reduce<FilesType>(
    (acc, file) => ({
      ...acc,
      [file.filename]: omit(['url'], file) as FileType,
    }),
    {},
  )
